import React from 'react';
import { Link } from 'react-router-dom';
import { AuditEntity } from 'src/api/types';
import { linkifyText } from 'src/common/Poppins';
import { store } from 'src/redux';

export const getEntityElements = (entity: AuditEntity | null) => {
  const colors = store.getState().theme.colors;
  if (!entity || (typeof entity !== 'object' && !Array.isArray(entity))) {
    return {
      path: null,
      name: '',
      typeName: '',
    };
  }

  const hasId = !!entity.id && entity.id !== 'null';
  const hasParent = !!entity.parent_id && entity.parent_id !== 'null';

  let path: string | null = null;

  switch (entity.type) {
    case 'assessment':
      if (hasId) path = `/risk-assessments/${entity.id}`;
      break;
    case 'assessment_action':
      path = `/risk-assessments/${entity.id}`;
      break;
    case 'assessment_comment':
      if (hasId) path = `/risk-assessments/${entity.id}`;
      break;

    case 'scenario':
      if (hasId && hasParent) path = `/risk-assessments/${entity.parent_id}/scenario/${entity.id}`;
      break;
    case 'scenario_action':
      path = `/risk-assessments/${entity.parent_id}/scenario/${entity.id}`;
      break;
    case 'scenario_comment':
      if (hasId && hasParent) path = `/risk-assessments/${entity.parent_id}/scenario/${entity.id}`;
      break;

    case 'incident':
      if (hasId) path = `/incidents/${entity.id}`;
      break;
    case 'incident_comment':
      if (hasId) path = `/incidents/${entity.id}`;
      break;
    case 'incident_action':
      path = `/incidents/${entity.id}`;
      break;
    case 'control':
      if (hasId) path = `/risk-management/control/${entity.id}`;
      break;
    case 'control_action':
      path = `/risk-management/control/${entity.id}`;
      break;
    case 'control_comment':
      if (hasId) path = `/risk-management/control/${entity.id}`;
      break;
  }

  const isDeleted = hasId ? entity.status === 'deleted' : false;
  if (isDeleted) path = null;

  const name = (
    <span
      css={`
        color: ${isDeleted ? colors.cflowerBlue : 'unset'};
      `}
    >
      <span>{entity.name}</span>
      {isDeleted && <i>{' (deleted)'}</i>}
    </span>
  );

  let typeName = '';

  switch (entity.type) {
    case 'incident':
      typeName = 'Incident';
      break;
    case 'user':
      typeName = 'User';
      break;
    case 'control':
      typeName = 'Control';
      break;
    case 'assessment':
      typeName = 'Assessment';
      break;
    case 'scenario':
      typeName = 'Scenario';
      break;
    case 'modifier':
      typeName = 'Modifier';
      break;
    case 'userEvent':
      typeName = 'User event';
      break;
    case 'incident_scenario':
      typeName = 'Incident scenario';
      break;
    case 'workspace':
      typeName = 'Workspace';
      break;
    case 'assessment_comment':
      typeName = 'Assessment';
      break;
    case 'scenario_comment':
      typeName = 'Scenario';
      break;
    case 'control_comment':
      typeName = 'Control';
      break;
    case 'incident_comment':
      typeName = 'Incident';
      break;
    case 'event_Action':
      typeName = 'Action';
      break;
    case 'incident_action':
      typeName = 'Incident';
      break;
    case 'assessment_action':
      typeName = 'Assessment';
      break;
    case 'scenario_action':
      typeName = 'Scenario';
      break;
    case 'control_action':
      typeName = 'Control';
      break;
    case 'member':
      typeName = 'Member';
      break;

    default:
      typeName = entity.type;
  }

  return { path, name, typeName, isDeleted };
};

type GenEntityJsx = (params: {
  renderName: string;
  entityName?: string;
  isDeleted?: boolean;
  path?: string | null;
}) => JSX.Element | string;

export const genEntityJsx: GenEntityJsx = ({ renderName, entityName, isDeleted, path }) => {
  const colors = store.getState().theme.colors;
  if (!entityName) {
    return <>{linkifyText(renderName)}</>;
  }

  const escapedEntityName = entityName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedEntityName})`, 'g');
  const parts = renderName.split(regex);

  return (
    <span>
      {parts.map((part, idx) =>
        part.toLowerCase() === entityName.toLowerCase() ? (
          <span
            key={idx}
            css={`
              color: ${isDeleted ? colors.cflowerBlue : 'unset'};
            `}
          >
            <span>
              {path ? (
                <Link
                  className="table-link"
                  to={path}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {part}
                </Link>
              ) : (
                linkifyText(part)
              )}
            </span>
            {isDeleted && <i>{' (deleted)'}</i>}
          </span>
        ) : (
          <React.Fragment key={idx}>{linkifyText(part)}</React.Fragment>
        ),
      )}
    </span>
  );
};
